// Central content + company config for ET Prime Pty Ltd
// Company details verified via ASIC Company Officeholders record.

export const COMPANY = {
  legalName: "ET Prime Pty Ltd",
  shortName: "ET Prime",
  acn: "689 474 459",
  abn: "31 689 474 459",
  afsl: "700116",
  afslStatus: "ET Prime Pty Ltd (ACN 689 474 459) has applied to ASIC for an Australian financial services licence authorising it, for wholesale clients only, to deal in and make a market in derivatives and foreign exchange contracts. The licence has not been granted. ET Prime does not currently provide financial services that require an AFSL and will not onboard counterparties, hold client money or quote prices until the licence is issued.",
  registered: "28 July 2025",
  jurisdiction: "Australia",
  addressLines: ["1 Margaret Street", "Sydney NSW 2000", "Australia"],
  email: "info@etprime.com.au",
  emails: {
    general: "info@etprime.com.au",
    legal: "legal@etprime.com.au",
    privacy: "privacy@etprime.com.au",
    compliance: "compliance@etprime.com.au",
  },
  phone: "+61 2 0000 0000",
  clientNotice: "Wholesale / Professional & Eligible Counterparty Clients Only",
  footerLine:
    "Wholesale / professional and eligible-counterparty clients only. ET Prime Pty Ltd ACN 689 474 459 ABN 31 689 474 459. Financial services are provided only to wholesale clients.",
};

export const NAV = [
  { label: "Solutions", to: "/solutions" },
  { label: "Technology", to: "/technology" },
  { label: "About", to: "/about" },
  { label: "Insights", to: "/insights" },
  { label: "Contact", to: "/contact" },
];

export const MANIFESTO = [
  {
    n: "01",
    title: "We are a counterparty, not a conduit",
    body: "We take a principal view of your flow. That alignment shapes every price, every risk decision, and every conversation we have with your desk.",
  },
  {
    n: "02",
    title: "Wholesale by design, without compromise",
    body: "We serve professional and eligible counterparties only. No retail products, no retail shortcuts — a single, serious relationship built for institutions.",
  },
  {
    n: "03",
    title: "Transparency is the whole point",
    body: "Once licensed, real-time analytics and reporting will be open to every client. In a market built on trust, visibility is not a feature we bolt on — it is the standard we are building to.",
  },
];

export const CREDIBILITY = [
  { value: "Wholesale Only", label: "Professional & eligible counterparties" },
  { value: "Multi-Asset", label: "FX · Metals · Indices · CFDs" },
  { value: "Application ref. 700116", label: "AFS licence — draft, not yet granted" },
  { value: "Australian", label: "ACN 689 474 459 · ABN 31 689 474 459" },
];

export const SEGMENTS = [
  {
    id: "broker-dealers",
    title: "Broker Dealers",
    line: "Aggregated wholesale liquidity for professional and institutional brokerages.",
    detail:
      "Customisable multi-asset liquidity with rule-based A-book and B-book flow management, alongside a suite of operational and risk tools — delivered under a single wholesale counterparty.",
  },
  {
    id: "hedge-funds",
    title: "Hedge Funds",
    line: "Institutional-grade liquidity and risk tools for dynamic strategies.",
    detail:
      "Multi-asset access and cross-margined CFD exposure across FX, indices, metals, energies and equities, with real-time risk analytics to support portfolio strategies — settled through a single relationship.",
  },
  {
    id: "prop-firms",
    title: "Proprietary Trading Firms",
    line: "Bespoke execution and direct access for sophisticated desks.",
    detail:
      "Direct access to aggregated liquidity with configurable routing and transparent execution analytics — engineered for systematic and discretionary desks that value control and efficiency.",
  },
  {
    id: "family-offices",
    title: "Family Offices",
    line: "Discreet, relationship-led access to global CFD markets.",
    detail:
      "A private, high-touch dealing relationship with tailored reporting and bespoke instrument coverage for family offices allocating across global markets.",
  },
];

export const ASSET_CLASSES = [
  { name: "Foreign Exchange", note: "Major, minor & exotic pairs" },
  { name: "Indices", note: "Global index CFDs" },
  { name: "Metals", note: "Precious & base metals" },
  { name: "Energies", note: "Crude, natural gas & products" },
  { name: "Equities", note: "Single-stock & ETF CFDs" },
  { name: "Commodities", note: "Softs & agricultural CFDs" },
];

export const SOLUTIONS = [
  {
    title: "Liquidity",
    body: "Robust, aggregated wholesale liquidity across FX, precious metals and CFDs, normalised into a single stream and supported by a seamless, reliable execution experience.",
  },
  {
    title: "Execution Models",
    body: "Choose pure agency, straight-through processing, or a hybrid market-making model. ET Prime's Australian financial services licence — which includes market making — is currently in draft and has not been granted; these execution models will be available once the licence is issued.",
  },
  {
    title: "Margin & Collateral",
    body: "Netted and cross-margined exposure with institutional collateral terms. Margin frameworks are structured around your book, not a one-size-fits-all schedule.",
  },
  {
    title: "Risk & Reporting",
    body: "Consolidated, real-time exposure and profitability reporting with statements and reconciliation files delivered to your specification.",
  },
];

// ET Prime platform — liquidity connectivity + risk analytics (ISAM Apex/Radar-style framing)
export const PRODUCTS = [
  {
    name: "Nexus",
    kind: "Liquidity & Execution Bridge",
    tagline: "One connection to wholesale liquidity.",
    body: "Nexus is ET Prime's multi-asset liquidity and execution bridge. It connects your desk to aggregated wholesale liquidity and gives you rule-based control over pricing, routing and hedging — combining a liquidity bridge and risk management in a single platform.",
    features: [
      "Rule-based A-book / B-book routing by symbol, account, volume and source",
      "Aggregated multi-asset liquidity across FX, metals, indices and CFDs",
      "LP reconciliation with automated break detection",
      "Full order-lifecycle tracking and position management",
    ],
  },
  {
    name: "Beacon",
    kind: "Real-Time Risk & Analytics",
    tagline: "Your entire book, in real time.",
    body: "Beacon consolidates exposure across your platforms, books and liquidity accounts into a single real-time view — so you can manage trading activity, monitor profitability and review execution with clarity.",
    features: [
      "Consolidated real-time exposure across servers and platforms",
      "Book performance, profitability and client-flow analytics",
      "Configurable alerting on exposure and risk thresholds",
      "Post-trade review and reporting to your specification",
    ],
  },
];

export const INSIGHTS = [
  {
    id: 1,
    category: "Market Structure",
    date: "June 2026",
    title: "Why prime-of-prime is the counterparty model of the next cycle",
    excerpt:
      "As the major banks continue to tighten direct prime brokerage access, the prime-of-prime intermediary has moved from convenience to necessity for mid-size brokers and funds.",
    read: "6 min read",
  },
  {
    id: 2,
    category: "Market Structure",
    date: "May 2026",
    title: "How liquidity aggregation works in an A-book / B-book model",
    excerpt:
      "A plain-English explainer of how a prime-of-prime constructs a price, manages flow, and where agency, straight-through processing and market-making sit in the chain.",
    read: "5 min read",
  },
  {
    id: 3,
    category: "Regulation",
    date: "May 2026",
    title: "The wholesale AFSL framework, explained",
    excerpt:
      "What ASIC's wholesale-client framework covers, how market-making authorisation would change a counterparty relationship, and why the wholesale-only distinction matters.",
    read: "7 min read",
  },
];
